import fetchRetry from "fetch-retry";

import { makeHttpError } from "./http-error";

export const CONVERSATION_BUSY_STATUS = 409;

export const CONVERSATION_BUSY_MESSAGE =
  "The assistant is still finishing your previous message. Please send it again in a moment.";

export const STREAMING_MAX_RETRIES = 5;

export const CONVERSATION_BUSY_RETRY_STATUSES = [CONVERSATION_BUSY_STATUS];

const MAX_BACKOFF_MS = 500;

const fetchWithRetry = fetchRetry(global.fetch);

export function parseRetryAfterMs(response: Response | null): number | null {
  const retryAfter = response?.headers.get("Retry-After");
  if (!retryAfter) return null;
  const seconds = Number(retryAfter);
  if (Number.isFinite(seconds)) return seconds * 1000;
  const dateMs = Date.parse(retryAfter);
  if (Number.isFinite(dateMs)) return Math.max(0, dateMs - Date.now());
  return null;
}

export function streamingRetryDelay(attempt: number, _error: Error | null, response: Response | null): number {
  const backoff = Math.min(100 * (attempt + 1), MAX_BACKOFF_MS);
  const retryAfterMs = parseRetryAfterMs(response);
  return retryAfterMs !== null ? Math.max(retryAfterMs, backoff) : backoff;
}

export function isConversationBusy(error: unknown): boolean {
  return (error as { status?: number } | null)?.status === CONVERSATION_BUSY_STATUS;
}

export async function safeFetchStreaming(
  url: string,
  options: RequestInit,
  retryStatuses: number[] = CONVERSATION_BUSY_RETRY_STATUSES,
): Promise<Response> {
  const response = await fetchWithRetry(url, {
    ...options,
    retries: STREAMING_MAX_RETRIES,
    retryDelay: streamingRetryDelay,
    retryOn: function (attempt: number, error: Error | null, response: Response | null) {
      if (attempt >= STREAMING_MAX_RETRIES) return false;
      if (response === null) return error?.name !== "AbortError";
      return retryStatuses.includes(response.status);
    },
    headers: (() => {
      const headers = new Headers(options.headers);
      if (!headers.has("Accept")) {
        headers.set("Accept", "text/plain");
      }
      return headers;
    })(),
  });

  if (!response.ok) {
    const errorBody = await response.text().catch(() => "");
    throw makeHttpError(response.status, errorBody);
  }

  const contentType = response.headers.get("content-type");
  if (contentType && !contentType.startsWith("text/")) {
    console.warn(`Expected text/* streaming response but got: ${contentType}`);
  }

  return response;
}
